/* ADPTGN scroll story: flowing backdrop, reveals, sticky story, carousels, perk picker, shop bar */
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const PERK_KEY = 'adptgn-perk';

  function readPerk() {
    try { return JSON.parse(localStorage.getItem(PERK_KEY) || 'null'); } catch (e) { return null; }
  }
  function savePerk(perk) {
    try { localStorage.setItem(PERK_KEY, JSON.stringify(perk)); } catch (e) {}
  }

  /* Flowing backdrop: the page color melts into each chapter's color scheme */
  function initFlow() {
    const chapters = Array.from(document.querySelectorAll('[data-mm-chapter]'));
    if (!chapters.length || !('IntersectionObserver' in window)) return;
    root.classList.add('mm-flow-on');
    const setColor = (el) => {
      const styles = getComputedStyle(el);
      const bg = styles.getPropertyValue('--color-background').trim();
      if (bg) root.style.setProperty('--mm-flow-bg', `rgb(${bg})`);
    };
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && setColor(entry.target)),
      { rootMargin: '-45% 0px -45% 0px' }
    );
    chapters.forEach((chapter) => observer.observe(chapter));
    setColor(chapters[0]);
  }

  /* Reveal: content is always visible; items play a rise-in animation when scrolled into view */
  function initReveal() {
    const items = Array.from(document.querySelectorAll('[data-mm-reveal]:not(.is-in)'));
    if (!items.length || reduceMotion || !('IntersectionObserver' in window) || document.body.classList.contains('shopify-design-mode')) {
      items.forEach((item) => item.classList.add('is-in'));
      return;
    }
    document.documentElement.classList.add('mm-reveal-on');
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in', 'is-revealing');
          observer.unobserve(entry.target);
        }),
      { threshold: 0 }
    );
    items.forEach((item) => {
      if (item.getBoundingClientRect().top < window.innerHeight) item.classList.add('is-in');
      else observer.observe(item);
    });
  }

  /* Sticky story: swap the pinned image as each step scrolls past */
  function initSteps() {
    document.querySelectorAll('[data-mm-steps]').forEach((container) => {
      const steps = container.querySelectorAll('[data-mm-step]');
      const media = container.querySelectorAll('[data-mm-step-media]');
      if (!steps.length || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(
        (entries) =>
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const index = entry.target.dataset.mmStep;
            media.forEach((m) => m.classList.toggle('is-active', m.dataset.mmStepMedia === index));
            steps.forEach((s) => s.classList.toggle('is-active', s.dataset.mmStep === index));
          }),
        { rootMargin: '-40% 0px -40% 0px' }
      );
      steps.forEach((step) => observer.observe(step));
    });
  }

  /* Swipe carousels with progress and arrows */
  function initCarousels() {
    document.querySelectorAll('[data-mm-carousel]').forEach((carousel) => {
      const track = carousel.querySelector('[data-mm-track]');
      const bar = carousel.querySelector('[data-mm-progress]');
      const prev = carousel.querySelector('[data-mm-prev]');
      const next = carousel.querySelector('[data-mm-next]');
      if (!track) return;
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const ratio = max > 0 ? track.scrollLeft / max : 1;
        if (bar) bar.style.transform = `scaleX(${Math.max(0.12, ratio)})`;
        if (prev) prev.disabled = track.scrollLeft <= 4;
        if (next) next.disabled = track.scrollLeft >= max - 4;
      };
      const step = () => (track.firstElementChild ? track.firstElementChild.getBoundingClientRect().width + 16 : track.clientWidth * 0.8);
      if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: reduceMotion ? 'auto' : 'smooth' }));
      if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: reduceMotion ? 'auto' : 'smooth' }));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* Pick your perk: remember the choice through the email signup, then reveal the code */
  function initPerks() {
    document.querySelectorAll('[data-mm-perk]').forEach((picker) => {
      const form = picker.querySelector('form');
      const options = picker.querySelectorAll('input[name="mm-perk-choice"]');
      const tagInput = picker.querySelector('[data-mm-perk-tag]');
      const reveal = picker.querySelector('[data-mm-perk-reveal]');
      const applyPath = picker.dataset.applyPath || '/';

      const showCode = (perk) => {
        if (!reveal || !perk) return;
        reveal.hidden = false;
        reveal.querySelector('[data-mm-perk-label]').textContent = perk.label;
        reveal.querySelector('[data-mm-perk-code]').textContent = perk.code;
        const apply = reveal.querySelector('[data-mm-perk-apply]');
        apply.href = `/discount/${encodeURIComponent(perk.code)}?redirect=${encodeURIComponent(applyPath)}`;
        const copy = reveal.querySelector('[data-mm-perk-copy]');
        if (copy && navigator.clipboard) {
          copy.addEventListener('click', () => {
            navigator.clipboard.writeText(perk.code).then(() => { copy.textContent = copy.dataset.copied; });
          });
        }
      };

      const choose = (input) => {
        if (tagInput) tagInput.value = `newsletter, perk-${input.value}`;
      };
      options.forEach((input) => input.addEventListener('change', () => choose(input)));
      const checked = picker.querySelector('input[name="mm-perk-choice"]:checked');
      if (checked) choose(checked);

      if (form) {
        form.addEventListener('submit', () => {
          const selected = picker.querySelector('input[name="mm-perk-choice"]:checked');
          if (selected) savePerk({ code: selected.dataset.code, label: selected.dataset.label });
        });
      }

      if (picker.dataset.success === 'true' || picker.dataset.returning === 'true') {
        showCode(readPerk());
      } else if (readPerk() && picker.dataset.showSaved === 'true') {
        showCode(readPerk());
      }
    });
  }

  /* Sticky shop bar */
  function initShopBar() {
    const bar = document.querySelector('[data-mm-shopbar]');
    if (!bar) return;
    const perk = readPerk();
    const perkLink = bar.querySelector('[data-mm-shopbar-perk]');
    if (perk && perkLink) {
      perkLink.textContent = perkLink.dataset.codeTemplate.replace('CODE', perk.code);
      perkLink.href = `/discount/${encodeURIComponent(perk.code)}?redirect=/`;
    }
    const footer = document.querySelector('.footer, footer');
    let footerVisible = false;
    if (footer && 'IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        footerVisible = entries[0].isIntersecting;
        toggle();
      }).observe(footer);
    }
    const toggle = () => {
      const show = window.scrollY > window.innerHeight * 0.7 && !footerVisible;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      bar.querySelectorAll('a, button').forEach((el) => (el.tabIndex = show ? 0 : -1));
    };
    window.addEventListener('scroll', toggle, { passive: true });
    toggle();
  }

  function init() {

    initReveal();
    initSteps();
    initCarousels();
    initPerks();
    initShopBar();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
